import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../App";
import { formatDateTime } from "../../i18n";
import { 
  MoreHorizontal, 
  MessageSquare, 
  Edit2, 
  Trash2, 
  Ticket,
  Loader2
} from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";

const MessageList = ({ 
  messages, 
  loading, 
  currentUserId, 
  typingUsers, 
  onOpenThread,
  channelId 
}) => {
  const { t, i18n } = useTranslation();
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [ticketMessage, setTicketMessage] = useState(null);
  const [ticketTitle, setTicketTitle] = useState("");
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [creatingTicket, setCreatingTicket] = useState(false);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Fetch programs for ticket creation
  const fetchPrograms = async () => {
    try {
      const response = await apiClient.get("/programs");
      setPrograms(response.data);
    } catch (e) {}
  };

  const handleEdit = (message) => {
    setEditingMessage(message);
    setEditBody(message.body);
  };

  const handleSaveEdit = async () => {
    if (!editingMessage || !editBody.trim()) return;
    
    try {
      await apiClient.patch(`/chat/messages/${editingMessage.id}`, { body: editBody });
      toast.success("Message updated");
      setEditingMessage(null);
      setEditBody("");
    } catch (error) {
      toast.error("Failed to update message");
    }
  };

  const handleDelete = async (messageId) => {
    try {
      await apiClient.delete(`/chat/messages/${messageId}`);
      toast.success("Message deleted");
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const handleCreateTicket = (message) => {
    setTicketMessage(message);
    setTicketTitle(message.body.slice(0, 50) + (message.body.length > 50 ? "..." : ""));
    fetchPrograms();
    setShowTicketDialog(true);
  };

  const submitCreateTicket = async () => {
    if (!ticketMessage || !selectedProgram || !ticketTitle.trim()) return;
    
    setCreatingTicket(true);
    try {
      const response = await apiClient.post(`/chat/messages/${ticketMessage.id}/create-ticket`, {
        program_id: selectedProgram,
        title: ticketTitle
      });
      toast.success("Ticket created successfully");
      setShowTicketDialog(false);
      setTicketMessage(null);
      setTicketTitle("");
      setSelectedProgram("");
    } catch (error) {
      toast.error("Failed to create ticket");
    } finally {
      setCreatingTicket(false);
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = new Date(message.created_at).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <div 
        ref={containerRef}
        className="flex-1 overflow-y-auto px-4 py-2"
        data-testid="message-list"
      >
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium mb-1">No messages yet</h3>
              <p className="text-sm text-muted-foreground">Be the first to send a message!</p>
            </div>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date}>
              {/* Date Divider */}
              <div className="flex items-center gap-4 my-4">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground font-medium">
                  {new Date(date).toLocaleDateString(i18n.language, { 
                    weekday: 'long', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </span>
                <div className="flex-1 h-px bg-border" />
              </div>
              
              {/* Messages */}
              {dateMessages.map((message, index) => {
                const isDeleted = !!message.deleted_at;
                const isEdited = !!message.edited_at;
                const isOwn = message.user_id === currentUserId;
                const showAvatar = index === 0 || 
                  dateMessages[index - 1]?.user_id !== message.user_id ||
                  new Date(message.created_at) - new Date(dateMessages[index - 1]?.created_at) > 300000;
                
                return (
                  <div 
                    key={message.id}
                    className={`group flex gap-3 py-1 hover:bg-secondary/30 rounded-lg px-2 -mx-2 ${
                      showAvatar ? "mt-4" : ""
                    }`}
                    data-testid={`message-${message.id}`}
                  >
                    {/* Avatar */}
                    <div className="w-9 flex-shrink-0">
                      {showAvatar && (
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                          {message.user_name?.charAt(0).toUpperCase() || "?"}
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {showAvatar && (
                        <div className="flex items-baseline gap-2 mb-0.5">
                          <span className="font-semibold text-sm">{message.user_name || "Unknown"}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(message.created_at).toLocaleTimeString(i18n.language, {
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </span>
                          {isEdited && !isDeleted && (
                            <span className="text-xs text-muted-foreground">(edited)</span>
                          )}
                        </div>
                      )}
                      
                      {isDeleted ? (
                        <p className="text-sm text-muted-foreground italic">This message was deleted</p>
                      ) : (
                        <div className="text-sm whitespace-pre-wrap break-words">
                          {message.body}
                        </div>
                      )}
                      
                      {/* Thread indicator */}
                      {message.reply_count > 0 && (
                        <button
                          onClick={() => onOpenThread(message)}
                          className="flex items-center gap-1 mt-1 text-xs text-primary hover:underline"
                        >
                          <MessageSquare className="w-3 h-3" />
                          {message.reply_count} {message.reply_count === 1 ? "reply" : "replies"}
                        </button>
                      )}
                    </div>
                    
                    {/* Actions */}
                    {!isDeleted && (
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-start gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => onOpenThread(message)}
                          title="Reply in thread"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleCreateTicket(message)}>
                              <Ticket className="w-4 h-4 mr-2" />
                              Create Ticket
                            </DropdownMenuItem>
                            {isOwn && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleEdit(message)}>
                                  <Edit2 className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(message.id)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
        
        {/* Typing indicators */}
        {typingUsers.length > 0 && (
          <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
            <span>
              {typingUsers.map(u => u.user_name).join(", ")} {typingUsers.length === 1 ? "is" : "are"} typing...
            </span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Edit Message Dialog */}
      <Dialog open={!!editingMessage} onOpenChange={() => setEditingMessage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Message</DialogTitle>
          </DialogHeader>
          <Textarea
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMessage(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Ticket Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Ticket from Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-secondary/50 rounded-lg text-sm">
              <p className="font-medium mb-1">{ticketMessage?.user_name}</p>
              <p className="text-muted-foreground">{ticketMessage?.body}</p>
            </div>
            <div className="space-y-2">
              <Label>Ticket Title</Label>
              <Input
                value={ticketTitle}
                onChange={(e) => setTicketTitle(e.target.value)}
                placeholder="Enter ticket title"
              />
            </div>
            <div className="space-y-2">
              <Label>Program</Label>
              <Select value={selectedProgram} onValueChange={setSelectedProgram}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a program" />
                </SelectTrigger>
                <SelectContent>
                  {programs.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.program_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTicketDialog(false)}>Cancel</Button>
            <Button 
              onClick={submitCreateTicket} 
              disabled={!selectedProgram || !ticketTitle.trim() || creatingTicket}
            >
              {creatingTicket ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MessageList;
